import { Head, Link } from "@inertiajs/react"
import { LinkExternal01 } from "@untitledui/icons"
import { format } from "date-fns"

import { PageHeader } from "@/components/console/page-header"
import { StatusBadge } from "@/components/ui/badge"
import { Notice } from "@/components/ui/notice"
import { Field, Fields, Panel } from "@/components/ui/panel"
import { AdminLayout } from "@/layouts/admin-layout"
import {
  adminCallListPath,
  adminCallListsPath,
  adminEnquiryPath,
} from "@/routes"

import { ChipStrip } from "../call-lists/_components/chip-strip"

import { AgendaSection } from "./_components/agenda-section"
import { CallActionBlock } from "./_components/call-action-block"
import { CohortsTable } from "./_components/cohorts-table"
import { LoggedCallsPanel } from "./_components/logged-calls-panel"
import { NoteForm } from "./_components/note-form"
import { PeoplePanel } from "./_components/people-panel"
import { TuningsPanel } from "./_components/tunings-panel"
import type { ShowProps } from "./types"

const formatStamp = (iso: string | null) =>
  iso ? format(new Date(iso), "d MMM yyyy 'at' HH:mm") : "—"

export default function AdminSchoolsShow({
  school,
  chips,
  agenda,
  notes,
  contacts,
  cohorts,
  funding,
  pipeline,
  enquiries,
  sequence,
  tunings,
  note,
  calling,
  logged_calls,
  scopes,
  list_context,
  mirror,
  live_contact,
  active_call,
}: ShowProps) {
  const backHref = list_context.scope_id
    ? adminCallListPath(list_context.scope_id)
    : adminCallListsPath()
  // The school's own scope: its sub-region's list, else its region's.
  const ownScope =
    scopes.find((s) => s.label === school.sub_region) ??
    scopes.find((s) => s.label === school.region)
  // The list the caller came from, when they came from one.
  const listLabel = list_context.scope_id
    ? scopes.find((s) => s.id === list_context.scope_id)?.label
    : undefined

  return (
    <AdminLayout>
      <Head title={`${school.name} · Call list`} />
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-10">
          <section className="flex min-w-0 flex-col gap-6">
            <PageHeader
              crumbs={[
                { label: "call list" },
                { label: "lists", href: adminCallListsPath() },
                ...(listLabel ? [{ label: listLabel, href: backHref }] : []),
                { label: school.name },
              ]}
              title={school.name}
            >
              <ChipStrip chips={chips} />
              <p className="text-secondary text-sm">
                {school.phone ? (
                  <span className="notation">{school.phone}</span>
                ) : (
                  "No switchboard on file"
                )}
                {school.ask_for
                  ? `, ask for ${school.ask_for.name}${school.ask_for.title ? ` (${school.ask_for.title})` : ""}`
                  : ", ring reception for a name"}
              </p>
              <p className="text-tertiary text-sm">
                {[school.sub_region ?? school.region, school.sector]
                  .filter(Boolean)
                  .join(" · ")}
                {school.owner.name
                  ? ` · owner ${school.owner.name}${school.owner.active === false ? " (inactive)" : ""}`
                  : ""}
                {school.salesforce_url && (
                  <>
                    {" · "}
                    <a
                      href={school.salesforce_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-secondary hover:text-brand-secondary_hover inline-flex items-center gap-1"
                    >
                      Salesforce <LinkExternal01 className="size-3" />
                    </a>
                  </>
                )}
              </p>
            </PageHeader>

            {school.stale && (
              <Notice tone="warning" role="status">
                Computed for {school.as_of}, on{" "}
                {formatStamp(school.computed_at)}. Season, due state and year
                levels may have moved since.
              </Notice>
            )}

            {enquiries.length > 0 && (
              <Notice tone="info" role="status">
                {enquiries.map((e) => (
                  <span key={e.id}>
                    <Link href={adminEnquiryPath(e.id)} className="underline">
                      {e.name ?? "Someone"}
                    </Link>{" "}
                    found us {Math.round(e.age_hours)} h ago
                    {e.sla_breached ? " (past the business-day SLA)" : ""}.{" "}
                  </span>
                ))}
              </Notice>
            )}

            {agenda.map((section) => (
              <AgendaSection key={section.id} section={section} />
            ))}

            <Panel title="Cohorts">
              <CohortsTable cohorts={cohorts} />
            </Panel>

            <Panel
              title="Funding"
              description={
                funding.known
                  ? undefined
                  : "Unknown: nothing prepaid and no deliverable matches this region."
              }
            >
              <Fields>
                <Field label="Prepaid programs">{funding.prepaid}</Field>
                <Field label="Deliverables with no region">
                  {funding.region_unknown}
                </Field>
              </Fields>
              {funding.matching.length > 0 && (
                <ul className="text-secondary flex flex-col gap-1 text-sm">
                  {funding.matching.map((m) => (
                    <li key={m.deliverable}>
                      {m.funder}: {m.remaining_to_link} unlinked of{" "}
                      {m.allocated}, {m.booked_not_delivered} booked
                      {m.ends ? `, ends ${m.ends}` : ""}
                      {m.region
                        ? ` · ${m.region}`
                        : m.national
                          ? " · national"
                          : ""}
                    </li>
                  ))}
                </ul>
              )}
              {funding.jobs_unfunded.length > 0 && (
                <p className="text-tertiary text-sm">
                  No funder linked yet: {funding.jobs_unfunded.join(", ")}. Ours
                  to fix.
                </p>
              )}
            </Panel>

            {pipeline.length > 0 && (
              <Panel title="Booked ahead">
                <ul className="flex flex-col gap-1.5">
                  {pipeline.map((job) => (
                    <li
                      key={job.job_id}
                      className="flex flex-wrap items-center gap-2 text-sm"
                    >
                      <span className="text-primary font-medium">
                        {job.job_name}
                      </span>
                      <span className="text-tertiary">{job.start}</span>
                      <span className="text-tertiary">{job.year_level}</span>
                      <StatusBadge tier="remote">{job.status}</StatusBadge>
                      <span className="text-tertiary">
                        {job.funder ?? "no funder linked"}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>
            )}

            <NoteForm schoolId={school.id} note={note} />
          </section>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-8 lg:h-fit">
            <CallActionBlock
              calling={calling}
              school={school}
              sequence={sequence}
              agenda={agenda}
              activeCall={active_call}
            />
            <LoggedCallsPanel calls={logged_calls} contacts={contacts} />
            <PeoplePanel contacts={contacts} live={live_contact} />
            <TuningsPanel
              schoolId={school.id}
              schoolName={school.name}
              scopeId={list_context.scope_id}
              scopes={scopes}
              defaultScopeId={ownScope?.id ?? null}
              tunings={tunings}
            />
            {notes && (
              <Panel title="Notes for the caller" headingLevel="h3">
                <p className="text-secondary text-sm whitespace-pre-line">
                  {notes}
                </p>
              </Panel>
            )}
            <p className="text-quaternary text-xs">
              Mirror synced {formatStamp(mirror.synced_at)}.
            </p>
          </aside>
        </div>
      </div>
    </AdminLayout>
  )
}
